<template>
  <div class="ret-login">
    <div v-if="!dataLoaded">
      <v-progress-circular indeterminate color="primary" class="ret-center-absolute"></v-progress-circular>
    </div>
    <div v-if="dataLoaded">
      <v-card-title class="ret-card">
        <v-card-title>
          <h3 class="headline">{{ $t('sign-in-caps') }}</h3>
        </v-card-title>
        <v-card-text>
          <form @keyup.enter="onLogin">
            <v-text-field
              v-model="email"
              :label="$t('email')"
              :error-messages="validationError.email ? $t(validationError.email) : undefined"
              type="email"
              ref="emailInput"
              data-test="login-email"
            ></v-text-field>
            <v-text-field
              v-model="password"
              :label="$t('password')"
              :error-messages="validationError.password ? $t(validationError.password) : undefined"
              type="password"
              ref="passwordInput"
              data-test="login-password"
            ></v-text-field>
          </form>
          <v-alert
            :value="newVersionError"
            icon="update"
            color="primary"
            outlined
          >{{ $t('login-again-observer-updated-notice') }}</v-alert>
          <v-btn
            @click="onLogin"
            color="primary"
            data-test="login-submit-button"
          >{{ $t('sign-in') }}</v-btn>
          <v-btn
            @click="resetMyPassword"
            text
            class="ret-right"
            data-test="reset-my-password"
          >{{ $t('reset-my-password') }}</v-btn>
        </v-card-text>
      </v-card-title>
    </div>

    <v-snackbar
      :bottom="true"
      v-model="resetPasswordSnackbar"
      :timeout="5000"
    >{{ $t('reset-password-email-sent') }}</v-snackbar>
  </div>
</template>

<script>
import { saveJwtToken, initUserInSession } from '../_initiators/login.es6'
import UsersService from '@/services/users.service.es6'

// const EMAIL_REGEX = '(?:[a-z0-9!#$%&\'*+/=?^_`{|}~-]+(?:\\.[a-z0-9!#$%&\'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\\])'

export default {
  data () {
    return {
      dataLoaded: false,
      email: '',
      password: '',
      rememberMe: true,
      validationError: {
        email: undefined,
        password: undefined
      },
      resetPasswordSnackbar: false
    }
  },
  computed: {
    newVersionError () {
      return this.$store.state.newVersionError
    }
  },
  created () {
    if (typeof Storage !== 'undefined') {
      const storedEmail = window.localStorage.getItem('lastUserEmail')
      if (storedEmail) {
        this.email = storedEmail
      }
    }

    if (this.$cookies.isKey('session')) {
      this.$router.push({ name: 'home' })
    }

    // Verify on BE whether the FE version is still valid
    const frontendVersion = window.localStorage.getItem(
      'retObserverFrontendVersion'
    )
    this.$store.dispatch('checkFrontendVersion', { frontendVersion })

    this.dataLoaded = true
  },
  mounted () {
    if (this.email) {
      this.$refs.passwordInput.$el.getElementsByTagName('input')[0].focus()
    } else {
      this.$refs.emailInput.$el.getElementsByTagName('input')[0].focus()
    }
  },
  methods: {
    // Actions
    async onLogin () {
      console.log('[login.vue][onLogin]')
      this.dataLoaded = false
      this.validationError.email = undefined
      this.validationError.password = undefined

      try {
        const frontendVersion = window.localStorage.getItem('retObserverFrontendVersion')
        const response = await this.$store.dispatch('authUser', {
          email: this.email,
          password: this.password,
          frontendVersion
        })
        // Create new session cookie storing the auth_token
        saveJwtToken(this, response.data)
        // Save user info in store
        const user = await initUserInSession(this)
        if (typeof Storage !== 'undefined') {
          window.localStorage.setItem('lastUserEmail', this.email)
        }

        if (this.$store.state.auth.currentUser.homePage) {
          UsersService.setHomePage(this.$store.state.auth.currentUser.homePage)
        }

        if (this.newVersionError) {
          this.$set(this.$store.state, 'newVersionError', false)
          window.location.reload(true)
        }

        // Re-route (if User.shouldChangePassword, re-route to page to change own password)
        if (user.shouldChangePassword) {
          this.$router.push({ name: 'user-settings' })
        } else {
          this.$router.push({ name: 'home' })
        }
      } catch (error) {
        this.newVersionError = false
        console.error(error)
        // Handling unsuccessful login
        // No re-route
        this.dataLoaded = true
        const errorMsg = error.response && error.response.status === 401
          ? 'Incorrect email or password'
          : 'Login not successful'

        // set newVersion only when it is available in response (means that new version error occured on BE)
        if (error.response.data.newVersion) {
          window.localStorage.setItem(
            'retObserverFrontendVersion',
            error.response.data.newVersion
          )
          this.newVersionError = true
        }
        this.validationError.email = errorMsg
        this.validationError.password = errorMsg
      }
    },
    resetMyPassword () {
      if (!this.email) {
        this.validationError.email = 'Email must be set for resetting password'
      } else {
        this.$store.dispatch('resetMyPassword', this.email)
        // User notification (email has been sent)
        // this.$refs.snackbar.open()
        this.resetPasswordSnackbar = true
      }
    }
  }
}
</script>
